import React, { useState, useEffect } from 'react';
import { UserDataStore } from '../store/userDataStore';
import { CalibrationLogbookEntry } from '../models/calibration-logbook-entry';
import TextBox, { TextBoxTypes } from 'devextreme-react/text-box';
import { NumberBox, NumberBoxTypes } from 'devextreme-react/number-box';
import DateBox, { DateBoxTypes } from 'devextreme-react/date-box';
import { CrudFunctions } from '../utilities/crud-functions';
import { FieldUpdate } from '../models/field-update';
import GlobalDialog from './Modal/GlobalDialog';
import { ModalMetadata } from '../models/modal-metadata';
import { ModalMessageType } from '../models/enums';

export interface Props {
    benchSheetId?: string;
}

const CALIBRATION_LOGBOOK_ENTRIES_ENTITY = "CalibrationLogBookEntries";

export default function CalibrationLogbook(props: Props) {
    const [dataLoaded, setDataLoaded] =
        useState<boolean>(false);

    const [canEdit, setCanEdit] =
        useState<boolean>(false);

    const [calibrationLogbookEntry, setCalibrationLogbookEntry] =
        useState<CalibrationLogbookEntry>();

    const [globalDialogMetadata, setGlobalDialogMetadata] =
        useState<ModalMetadata>();

    const [globalDialogVisible, setGlobalDialogVisible] =
        useState<boolean>();

    useEffect(() => {
        setAuthorizations();
        getCalibrationLogbookEntry();
    }, []);

    return (
        <div>
            <div className="lq-header pw-margin-top-05">
                <div className="lq-header-title">Calibration Logbook Entry</div>
            </div>

            <div className="pw-form-section">
                <div className="pw-comp-row">
                    <div className="pw-form-unit pw-comp-15">
                        <div className="pw-form-unit-label">LogBook ID</div>
                            <TextBox id="logbookId" className="textbox"
                                readOnly={!canEdit}
                                placeholder='NA'
                                onValueChanged={logbookIdChanged}
                                value={calibrationLogbookEntry && calibrationLogbookEntry.logbookId ?
                                    calibrationLogbookEntry.logbookId : ''} />
                    </div>

                    <div className="pw-form-unit pw-comp-15">
                        <div className="pw-form-unit-label">Page Number</div>
                        <NumberBox id="pageNumber" className="textbox"
                            readOnly={!canEdit}
                            placeholder='NA'
                            onValueChanged={logbookPageNumberChanged}
                            value={calibrationLogbookEntry && calibrationLogbookEntry.pageNumber ?
                                calibrationLogbookEntry.pageNumber : undefined} />
                    </div>

                    <div className="pw-form-unit pw-comp-15">
                        <div className="pw-form-unit-label">Calibration Date</div>
                        <DateBox id="calibrationDate" type="date" readOnly={!canEdit}
                            displayFormat="MM/dd/yyyy"
                            showClearButton={true}
                            defaultValue={null}
                            onValueChanged={logbookCalibrationDateChanged}
                            value={calibrationLogbookEntry && calibrationLogbookEntry.calibrationDate ?
                                calibrationLogbookEntry.calibrationDate : undefined}
                            placeholder="na" />
                    </div>
                </div>
            </div>

            {globalDialogVisible &&
                <GlobalDialog metadata={globalDialogMetadata} onClose={clearGlobalDialog} />
            }

        </div>
    );

    async function logbookIdChanged(e: TextBoxTypes.ValueChangedEvent) {
        if (!dataLoaded)
            return;

        const currentValue = calibrationLogbookEntry.logbookId;

        if (currentValue === e.value)
            return;
        else if (!currentValue && !e.value)
            return;
        else {
            setCalibrationLogbookEntry({ ...calibrationLogbookEntry, logbookId: e.value });

            if (e.element && e.value) {
                const caller = e.element.id;

                updateField(caller, e.value);
            }
        }
    };

    function logbookPageNumberChanged(e: NumberBoxTypes.ValueChangedEvent) {
        if (!dataLoaded)
            return;

        const currentValue = calibrationLogbookEntry.pageNumber;

        if (currentValue === e.value)
            return;
        else if (!currentValue && !e.value)
            return;
        else {
            setCalibrationLogbookEntry({ ...calibrationLogbookEntry, pageNumber: e.value });

            if (e.element && e.value) {
                const caller = e.element.id;

                updateField(caller, e.value);
            }
        }
    }

    function logbookCalibrationDateChanged(e: DateBoxTypes.ValueChangedEvent) {
        if (!dataLoaded)
            return;

        const currentValue = calibrationLogbookEntry.calibrationDate;

        if (currentValue === e.value)
            return;
        else if (!currentValue && !e.value)
            return;
        else {
            setCalibrationLogbookEntry({ ...calibrationLogbookEntry, calibrationDate: e.value });

            if (e.element && e.value) {
                const caller = e.element.id;

                updateField(caller, e.value);
            }
        }
    }

    async function updateField(caller: string, val: any) {
        const fieldUpdate: FieldUpdate = {
            entity: CALIBRATION_LOGBOOK_ENTRIES_ENTITY,
            rowId: calibrationLogbookEntry.calibrationLogbookEntryId,
            field: caller,
            value: val
        };

        await updateCalibrationLogbookEntryField(fieldUpdate)
            .then(data => {
            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Updating Logbook Entry");
            });
    }
    
    function setAuthorizations() {
        const user = UserDataStore.getCurrentUser();

        const canEditRoles = ["SA", "MF-A", "MF-L", "MF-T", "PP-A", "PP-L", "PP-T", "DIST-A", "BA-A", "QCP-A", "QCD-A", "RD-A", "RD-L", "RD-T"];
        const canEdit = canEditRoles.some(r => user.roleCodes && user.roleCodes.includes(r));

        setCanEdit(canEdit);
    }

    async function getCalibrationLogbookEntry() {
        if (props.benchSheetId) {
            await CrudFunctions.getData(`benchSheets/calibrationlogbookentry/${props.benchSheetId}`)
                .then(data => {
                    setCalibrationLogbookEntry(data);

                    setDataLoaded(true);
                })
                .catch((res) => {
                    showMessage(res, ModalMessageType.Error, "Error Retrieving Logbook Entries");
                });
        }
    }

    async function updateCalibrationLogbookEntryField(fieldUpdate: FieldUpdate) {
        return await CrudFunctions.putData("benchSheets/calibrationlogbookentryfield", fieldUpdate);
            
    }

    function showMessage(res: any, mode: ModalMessageType, title: string) {
        let mm: ModalMetadata = {
            error: res,
            mode: mode,
            title: title
        };

        setGlobalDialogMetadata(mm);
        setGlobalDialogVisible(true);
    }

    function clearGlobalDialog() {
        setGlobalDialogMetadata(null);
        setGlobalDialogVisible(false);
    }

}

